import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { apiUrl } from '../core/api-client';
import { AnalyticsService } from '../core/analytics.service';
import { LocalizationService } from '../core/localization.service';
import {
  DsAlertComponent,
  DsAnswerOptionComponent,
  DsAssessmentProgressComponent,
  DsAssessmentTimerComponent,
  DsBadgeComponent,
  DsButtonDirective,
  DsCardDirective,
  DsFieldComponent,
  DsIconComponent,
  DsInputDirective,
  DsProgressComponent,
  DsQuestionCardComponent,
  DsQuestionNavigatorComponent,
  DsResultCardComponent,
  DsScoreVisualizationComponent,
  DsSelectDirective,
  DsStateComponent,
} from '../ui';
import {
  PlatformApi,
  PublicIqCompletion,
  PublicIqQuestion,
  PublicIqResult,
  PublicIqSession,
} from '../core/platform-api';

type TestStage = 'language' | 'loading' | 'questions' | 'identity' | 'result' | 'error';

@Component({
  selector: 'app-iq-test-page',
  imports: [
    DsAlertComponent,
    DsAnswerOptionComponent,
    DsAssessmentProgressComponent,
    DsAssessmentTimerComponent,
    DsBadgeComponent,
    DsButtonDirective,
    DsCardDirective,
    DsFieldComponent,
    DsIconComponent,
    DsInputDirective,
    DsProgressComponent,
    DsQuestionCardComponent,
    DsQuestionNavigatorComponent,
    DsResultCardComponent,
    DsScoreVisualizationComponent,
    DsSelectDirective,
    DsStateComponent,
  ],
  templateUrl: './iq-test-page.html',
  styleUrl: './iq-test-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IqTestPage implements OnDestroy {
  private readonly api = inject(PlatformApi);
  private readonly analytics = inject(AnalyticsService);
  private readonly i18n = inject(LocalizationService);
  private readonly document = inject(DOCUMENT);
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  protected readonly language = this.i18n.language;
  protected readonly languages = this.i18n.languages;
  protected readonly stage = signal<TestStage>('language');
  protected readonly session = signal<PublicIqSession | null>(null);
  protected readonly result = signal<PublicIqCompletion | null>(null);
  protected readonly secondsRemaining = signal(35 * 60);
  protected readonly busy = signal(false);
  protected readonly savingAnswer = signal(false);
  protected readonly questionEntering = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly selectedLanguage = signal(this.language());
  protected readonly firstName = signal('');
  protected readonly lastName = signal('');
  protected readonly dateOfBirth = signal('');
  protected readonly identityError = signal('');
  protected readonly resultEmail = signal('');
  protected readonly resultEmailSent = signal(false);
  protected readonly resultEmailError = signal('');
  protected readonly feedbackRating = signal(0);
  protected readonly feedbackComment = signal('');
  protected readonly feedbackSent = signal(false);
  protected readonly feedbackBusy = signal(false);
  protected readonly feedbackError = signal('');
  protected readonly currentQuestion = computed(() => this.session()?.question ?? null);
  protected readonly progress = computed(() => {
    const question = this.currentQuestion();
    return question ? Math.round(((question.position + 1) / question.total) * 100) : 0;
  });
  protected readonly percentile = computed(() => {
    const estimate = this.result()?.estimate ?? 100;
    const z = (estimate - 100) / 15;
    const sign = z < 0 ? -1 : 1;
    const x = Math.abs(z) / Math.sqrt(2);
    const t = 1 / (1 + 0.3275911 * x);
    const erf =
      sign *
      (1 -
        ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t +
          0.254829592) *
          t *
          Math.exp(-x * x));
    return Math.max(1, Math.min(99, Math.round(((1 + erf) / 2) * 100)));
  });
  protected readonly timeLabel = computed(() => {
    const minutes = Math.floor(this.secondsRemaining() / 60);
    const seconds = this.secondsRemaining() % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  });
  private timer?: ReturnType<typeof setInterval>;
  private revealTimer?: ReturnType<typeof setTimeout>;
  private answerSaveQueue: Promise<void> = Promise.resolve();
  private readonly questionCard = viewChild(DsQuestionCardComponent);
  private readonly questionDurations = new Map<string, number>();
  private readonly trackedMilestones = new Set<number>();
  private questionViewedAt = 0;
  private assessmentStartedAt = 0;
  private identityReached = false;
  private flowFinalized = false;
  private abandonmentTracked = false;
  private readonly handlePageHide = () => this.trackAbandonment('pagehide');

  constructor() {
    this.document.defaultView?.addEventListener('pagehide', this.handlePageHide);
    this.analytics.track('iq_test_viewed', { language: this.language() });
    effect(() => {
      this.language();
      this.applySeo();
    });
    effect(() => {
      const question = this.currentQuestion();
      const card = this.questionCard();
      if (this.stage() === 'questions' && question && card) card.focusHeading();
    });
  }

  protected t(key: string): string {
    return this.i18n.t(key);
  }

  protected async startTest(): Promise<void> {
    this.stage.set('loading');
    this.errorMessage.set('');
    this.result.set(null);
    this.firstName.set('');
    this.lastName.set('');
    this.dateOfBirth.set('');
    this.identityError.set('');
    this.questionDurations.clear();
    this.trackedMilestones.clear();
    this.questionViewedAt = 0;
    this.assessmentStartedAt = Date.now();
    this.identityReached = false;
    this.flowFinalized = false;
    this.abandonmentTracked = false;
    if (!this.analytics.currentAssessmentFlowId()) {
      this.analytics.beginAssessmentFlow(this.language());
    }
    this.analytics.track('iq_test_start_requested', { language: this.language() });
    try {
      const session = await this.api.startPublicIqTest(this.language());
      this.session.set(session);
      this.stage.set('questions');
      this.analytics.track('iq_test_started', {
        language: session.language,
        question_total: session.question.total,
      });
      this.trackProductEvent('test_started');
      this.revealQuestion();
      this.syncTimer(session.expiresAtUtc);
    } catch {
      this.analytics.track('iq_test_error', { operation: 'start', stage: 'loading' });
      this.errorMessage.set(this.t('iq.error'));
      this.stage.set('error');
    }
  }

  protected async selectLanguage(value: string): Promise<void> {
    if (this.busy()) return;
    const previousLanguage = this.language();
    this.selectedLanguage.set(value);
    this.analytics.track('iq_language_selected', {
      from_language: previousLanguage,
      to_language: value,
    });
    this.busy.set(true);
    try {
      await this.i18n.setLanguage(value);
    } finally {
      this.busy.set(false);
    }
  }

  protected async beginTest(): Promise<void> {
    if (this.busy()) return;
    if (this.language() !== this.selectedLanguage()) {
      await this.selectLanguage(this.selectedLanguage());
    }
    this.analytics.beginAssessmentFlow(this.language());
    await this.startTest();
  }

  protected selectAnswer(question: PublicIqQuestion, option: number): void {
    const session = this.session();
    if (
      !session ||
      session.question.id !== question.id ||
      session.question.selectedOption === option
    )
      return;

    const answerChanged = session.question.selectedOption !== null;
    this.analytics.track('iq_answer_selected', {
      position: question.position + 1,
      question_total: question.total,
      domain: question.domain.toLowerCase(),
      answer_changed: answerChanged,
      dwell_ms: this.currentQuestionDwellMs(),
    });
    this.session.set({
      ...session,
      answeredCount: session.answeredCount + (session.question.selectedOption === null ? 1 : 0),
      question: { ...session.question, selectedOption: option },
    });
    this.savingAnswer.set(true);
    this.answerSaveQueue = this.answerSaveQueue
      .catch(() => undefined)
      .then(() => this.api.answerPublicIqQuestion(session.sessionId, question.id, option))
      .then(
        () => undefined,
        () => {
          this.errorMessage.set(this.t('iq.error'));
        },
      );
    const pending = this.answerSaveQueue;
    void pending.finally(() => {
      if (this.answerSaveQueue === pending) this.savingAnswer.set(false);
    });
  }

  protected async move(offset: number): Promise<void> {
    const session = this.session();
    const question = session?.question;
    if (!session || !question || this.busy()) return;
    const nextPosition = question.position + offset;
    if (nextPosition >= question.total) {
      this.captureQuestionTime();
      this.analytics.track('iq_questionnaire_completed', {
        answered_count: session.answeredCount,
        question_total: question.total,
      });
      this.prepareCompletion('answered_all');
      return;
    }
    if (nextPosition < 0) return;
    this.busy.set(true);
    this.captureQuestionTime();
    try {
      await this.answerSaveQueue;
      const nextSession = await this.api.getPublicIqQuestion(session.sessionId, nextPosition);
      this.session.set(nextSession);
      this.analytics.track('iq_question_navigated', {
        direction: offset > 0 ? 'next' : 'previous',
        from_position: question.position + 1,
        to_position: nextSession.question.position + 1,
      });
      this.revealQuestion();
    } catch {
      this.questionViewedAt = Date.now();
      this.analytics.track('iq_test_error', { operation: 'navigate', stage: 'questions' });
      this.errorMessage.set(this.t('iq.error'));
    } finally {
      this.busy.set(false);
    }
  }

  protected domainLabel(domain: PublicIqQuestion['domain']): string {
    return this.i18n.domain(domain);
  }

  protected bandLabel(result: PublicIqResult): string {
    return this.i18n.band(result.band);
  }

  protected domainAccuracy(correct: number, total: number): number {
    return total ? Math.round((correct / total) * 100) : 0;
  }

  protected domainStatus(correct: number, total: number): string {
    const accuracy = this.domainAccuracy(correct, total);
    if (accuracy >= 80) return this.t('iq.strength');
    if (accuracy >= 60) return this.t('iq.balanced');
    return this.t('iq.practiceFocus');
  }

  protected strongestDomain(score: PublicIqCompletion): string {
    const strongest = [...score.domains].sort(
      (left, right) => right.correct / right.total - left.correct / left.total,
    )[0];
    return strongest ? this.domainLabel(strongest.domain) : this.t('iq.notAvailable');
  }

  protected focusDomain(score: PublicIqCompletion): string {
    const focus = [...score.domains].sort(
      (left, right) => left.correct / left.total - right.correct / right.total,
    )[0];
    return focus ? this.domainLabel(focus.domain) : this.t('iq.notAvailable');
  }

  protected practiceUrl(domain: PublicIqQuestion['domain']): string {
    const slug: Record<PublicIqQuestion['domain'], string> = {
      Abstract: 'matrix-reasoning-strategies',
      Numerical: 'number-sequence-strategies',
      Verbal: 'verbal-analogy-strategies',
      Spatial: 'cube-rotation-puzzle-strategies',
    };
    return `/en/content/${slug[domain]}/`;
  }
  protected updateIdentity(field: 'firstName' | 'lastName' | 'dateOfBirth', value: string): void {
    if (field === 'firstName') this.firstName.set(value);
    if (field === 'lastName') this.lastName.set(value);
    if (field === 'dateOfBirth') this.dateOfBirth.set(value);
    this.identityError.set('');
  }

  protected candidateName(): string {
    return `${this.firstName().trim()} ${this.lastName().trim()}`.trim();
  }

  protected maximumDateOfBirth(): string {
    return new Date().toISOString().slice(0, 10);
  }

  protected certificateUrl(): string {
    return apiUrl(`/api/v1/public-iq-tests/${this.session()?.sessionId}/certificate.pdf`);
  }

  protected verificationUrl(): string {
    return `/verify/${this.session()?.sessionId}`;
  }

  protected async finishTest(): Promise<void> {
    const session = this.session();
    if (!session || this.busy()) return;
    const firstName = this.firstName().trim();
    const lastName = this.lastName().trim();
    const dateOfBirth = this.dateOfBirth();
    const birthDate = Date.parse(`${dateOfBirth}T00:00:00Z`);
    if (
      firstName.length < 1 ||
      firstName.length > 80 ||
      lastName.length < 1 ||
      lastName.length > 80 ||
      firstName.length + lastName.length + 1 > 160 ||
      !dateOfBirth ||
      !Number.isFinite(birthDate) ||
      birthDate > Date.now()
    ) {
      this.analytics.track('iq_identity_validation_failed', {
        first_name_valid: firstName.length >= 1 && firstName.length <= 80,
        last_name_valid: lastName.length >= 1 && lastName.length <= 80,
        birth_date_valid:
          Boolean(dateOfBirth) && Number.isFinite(birthDate) && birthDate <= Date.now(),
      });
      this.identityError.set(this.t('iq.identityRequired'));
      return;
    }
    this.busy.set(true);
    this.stopTimer();
    this.analytics.track('iq_submission_started', {
      answered_count: session.answeredCount,
      question_total: session.question.total,
    });
    try {
      await this.answerSaveQueue;
      const completion = await this.api.completePublicIqTest(
        session.sessionId,
        firstName,
        lastName,
        dateOfBirth,
      );
      this.result.set({
        ...completion,
        questions: completion.questions.map((question) => ({
          ...question,
          timeSpentSeconds: this.questionDurations.get(question.id) ?? 0,
        })),
      });
      this.stage.set('result');
      this.flowFinalized = true;
      this.analytics.track('iq_test_completed', {
        language: session.language,
        answered_count: session.answeredCount,
        question_total: session.question.total,
        duration_seconds: this.assessmentDurationSeconds(),
      });
      this.trackProductEvent('test_completed');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      this.analytics.track('iq_test_error', { operation: 'submit', stage: 'identity' });
      this.identityError.set(this.t('iq.error'));
    } finally {
      this.busy.set(false);
    }
  }

  protected async emailResult(): Promise<void> {
    const sessionId = this.session()?.sessionId;
    const email = this.resultEmail().trim();
    if (!sessionId || !email.includes('@') || this.busy()) {
      this.resultEmailError.set('Enter a valid email address.');
      return;
    }
    this.busy.set(true);
    this.resultEmailError.set('');
    try {
      await this.api.emailPublicIqResult(sessionId, email);
      this.resultEmailSent.set(true);
      this.analytics.track('iq_result_emailed');
      this.trackProductEvent('result_emailed');
    } catch {
      this.analytics.track('iq_test_error', { operation: 'email_result', stage: 'result' });
      this.resultEmailError.set('We could not queue the email. Please try again.');
    } finally {
      this.busy.set(false);
    }
  }
  protected async submitFeedback(): Promise<void> {
    const testId = this.session()?.sessionId;
    if (!testId || this.feedbackRating() < 1 || this.feedbackSent()) return;
    this.feedbackBusy.set(true);
    try {
      await this.api.submitAssessmentFeedback(
        testId,
        this.feedbackRating(),
        this.feedbackComment().trim(),
      );
      this.feedbackSent.set(true);
      this.analytics.track('iq_feedback_submitted', { rating: this.feedbackRating() });
    } catch {
      this.analytics.track('iq_test_error', { operation: 'feedback', stage: 'result' });
      this.feedbackError.set('We could not save your feedback. Please try again.');
    } finally {
      this.feedbackBusy.set(false);
    }
  }

  protected trackProductEvent(name: string): void {
    void Promise.resolve()
      .then(() => this.api.recordProductEvent(name))
      .catch(() => undefined);
  }
  protected trackResultAction(action: 'certificate_downloaded' | 'verification_opened'): void {
    this.analytics.track(`iq_${action}`);
    this.trackProductEvent(action);
  }

  protected chooseAnotherTest(): void {
    this.analytics.track('iq_restart_selected');
    this.analytics.endAssessmentFlow();
    this.session.set(null);
    this.result.set(null);
    this.selectedLanguage.set(this.language());
    this.stage.set('language');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private prepareCompletion(reason: 'answered_all' | 'time_expired'): void {
    this.stopTimer();
    if (!this.identityReached) {
      this.identityReached = true;
      const session = this.session();
      this.analytics.track('iq_identity_reached', {
        reason,
        answered_count: session?.answeredCount ?? 0,
        question_total: session?.question.total ?? 0,
        duration_seconds: this.assessmentDurationSeconds(),
      });
    }
    this.stage.set('identity');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private applySeo(): void {
    const canonical = 'https://www.iqlabtest.com/';
    const pageTitle = 'Online IQ Test: 40-Question Cognitive Assessment | IQLabTest';
    const description =
      'Take a professional 40-question online IQ test covering abstract, numerical, verbal, and spatial reasoning, with multilingual questions and a verifiable certificate.';

    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow, max-image-preview:large' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: canonical + 'assets/brand/iqlabtest-social-card.png',
    });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'IQLabTest professional online IQ test',
    });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({
      name: 'twitter:image',
      content: canonical + 'assets/brand/iqlabtest-social-card.png',
    });
    this.meta.updateTag({
      name: 'twitter:image:alt',
      content: 'IQLabTest professional online IQ test',
    });

    const link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (link) link.href = canonical;
    this.document.head
      .querySelectorAll('link[data-iqlabtest-alternate]')
      .forEach((element) => element.remove());
    for (const id of [
      'content-structured-data',
      'about-structured-data',
      'legal-structured-data',
    ]) {
      this.document.getElementById(id)?.remove();
    }
  }

  private syncTimer(expiresAtUtc: string): void {
    this.stopTimer();
    const update = () => {
      const remaining = Math.max(0, Math.ceil((Date.parse(expiresAtUtc) - Date.now()) / 1000));
      this.secondsRemaining.set(remaining);
      if (remaining === 0) this.prepareCompletion('time_expired');
    };
    update();
    this.timer = setInterval(update, 1000);
  }

  private revealQuestion(): void {
    if (this.revealTimer) clearTimeout(this.revealTimer);
    this.questionEntering.set(true);
    this.questionViewedAt = Date.now();
    const question = this.currentQuestion();
    if (question) {
      const position = question.position + 1;
      this.analytics.track('iq_question_viewed', {
        position,
        question_total: question.total,
        domain: question.domain.toLowerCase(),
      });
      if ([1, 10, 20, 30, 40].includes(position) && !this.trackedMilestones.has(position)) {
        this.trackedMilestones.add(position);
        this.analytics.track(`iq_question_${position}_reached`, {
          answered_count: this.session()?.answeredCount ?? 0,
          question_total: question.total,
        });
      }
    }
    this.revealTimer = setTimeout(() => this.questionEntering.set(false), 360);
  }

  private captureQuestionTime(): void {
    const question = this.currentQuestion();
    if (!question || !this.questionViewedAt) return;
    const elapsedMs = this.currentQuestionDwellMs();
    const elapsedSeconds = Math.max(1, Math.round(elapsedMs / 1000));
    this.questionDurations.set(
      question.id,
      (this.questionDurations.get(question.id) ?? 0) + elapsedSeconds,
    );
    this.analytics.track('iq_question_time', {
      position: question.position + 1,
      question_total: question.total,
      domain: question.domain.toLowerCase(),
      duration_ms: elapsedMs,
      answered: question.selectedOption !== null,
    });
    this.questionViewedAt = 0;
  }

  private currentQuestionDwellMs(): number {
    if (!this.questionViewedAt) return 0;
    return Math.max(0, Math.min(2_100_000, Date.now() - this.questionViewedAt));
  }

  private assessmentDurationSeconds(): number {
    if (!this.assessmentStartedAt) return 0;
    return Math.max(0, Math.round((Date.now() - this.assessmentStartedAt) / 1000));
  }

  private trackAbandonment(reason: 'pagehide' | 'component_destroyed'): void {
    const session = this.session();
    if (
      this.abandonmentTracked ||
      this.flowFinalized ||
      !session ||
      !['questions', 'identity', 'error'].includes(this.stage())
    )
      return;
    this.captureQuestionTime();
    this.abandonmentTracked = true;
    this.analytics.track('iq_test_abandoned', {
      reason,
      stage: this.stage(),
      current_position: session.question.position + 1,
      answered_count: session.answeredCount,
      question_total: session.question.total,
      duration_seconds: this.assessmentDurationSeconds(),
    });
  }
  private stopTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }

  ngOnDestroy(): void {
    this.trackAbandonment('component_destroyed');
    this.document.defaultView?.removeEventListener('pagehide', this.handlePageHide);
    this.stopTimer();
    if (this.revealTimer) clearTimeout(this.revealTimer);
  }
}
